/**
 * =============================================================================
 * Service accounts i18n (SK + EN) — FE issue #28 / BE #64
 * =============================================================================
 *
 * Texts for the system_admin-only "Service accounts" page that manages
 * machine identities used by automated monitoring. Language resolution is
 * shared with `promotionI18n.ts` (stored preference > browser locale).
 */

import { getLang, type Lang } from './promotionI18n'

export type { Lang }

interface BiText {
  sk: string
  en: string
}

export const SERVICE_ACCOUNT_TEXTS = {
  navTitle: { sk: 'Servisné účty', en: 'Service accounts' },
  pageTitle: { sk: 'Servisné účty', en: 'Service accounts' },
  intro: {
    sk: 'Servisné účty slúžia výhradne na automatizovaný monitoring platformy. Majú iba čítací prístup k stavu služieb (health/konektivita) a k agregovaným počtom. Nemôžu sa prihlásiť do tohto portálu a nemajú prístup k žiadnym údajom pacientov.',
    en: 'Service accounts are used only for automated platform monitoring. They have read-only access to service health/connectivity and aggregate counts. They cannot sign in to this portal and have no access to any patient data.',
  },
  noDeleteHint: {
    sk: 'Servisné účty sa nemažú — nepotrebný účet deaktivujte.',
    en: 'Service accounts are never deleted — deactivate an account you no longer need.',
  },
  addAccount: { sk: '+ Nový servisný účet', en: '+ New service account' },
  createTitle: { sk: 'Nový servisný účet', en: 'New service account' },
  fieldName: { sk: 'Názov *', en: 'Name *' },
  fieldNameHint: {
    sk: '3–63 znakov: malé písmená, číslice a pomlčka; musí začínať písmenom alebo číslicou (napr. prod-monitor).',
    en: '3–63 characters: lowercase letters, digits and hyphens; must start with a letter or digit (e.g. prod-monitor).',
  },
  fieldNameInvalid: {
    sk: 'Neplatný názov. Povolené sú malé písmená, číslice a pomlčka (3–63 znakov).',
    en: 'Invalid name. Use lowercase letters, digits and hyphens (3–63 characters).',
  },
  fieldDescription: { sk: 'Popis', en: 'Description' },
  fieldDescriptionPlaceholder: {
    sk: 'Na čo účet slúži (napr. PROD monitorovacia sonda)…',
    en: 'What the account is for (e.g. PROD monitoring probe)…',
  },
  create: { sk: 'Vytvoriť', en: 'Create' },
  creating: { sk: 'Vytvára sa…', en: 'Creating…' },
  cancel: { sk: 'Zrušiť', en: 'Cancel' },
  close: { sk: 'Zavrieť', en: 'Close' },
  working: { sk: 'Prebieha…', en: 'Working…' },

  // --- Table ----------------------------------------------------------------
  colName: { sk: 'Názov', en: 'Name' },
  colDescription: { sk: 'Popis', en: 'Description' },
  colRole: { sk: 'Rola', en: 'Role' },
  colType: { sk: 'Typ', en: 'Type' },
  colStatus: { sk: 'Stav', en: 'Status' },
  colTokenPrefix: { sk: 'Token', en: 'Token' },
  colCreated: { sk: 'Vytvorený', en: 'Created' },
  colRotated: { sk: 'Posledná rotácia', en: 'Last rotated' },
  colLastUsed: { sk: 'Posledné použitie', en: 'Last used' },
  colActions: { sk: 'Akcie', en: 'Actions' },
  roleMonitor: { sk: 'Monitorovací', en: 'Monitoring' },
  typeServiceAccount: { sk: 'Servisný účet (nie osoba)', en: 'Service account (not a person)' },
  statusActive: { sk: 'Aktívny', en: 'Active' },
  statusInactive: { sk: 'Neaktívny', en: 'Inactive' },
  never: { sk: 'nikdy', en: 'never' },
  fromIp: { sk: 'z IP {ip}', en: 'from IP {ip}' },
  createdBy: { sk: 'vytvoril {who}', en: 'by {who}' },
  emptyList: { sk: 'Zatiaľ žiadne servisné účty.', en: 'No service accounts yet.' },
  searchPlaceholder: { sk: 'Hľadať podľa názvu alebo popisu…', en: 'Search by name or description…' },
  loadFailed: { sk: 'Servisné účty sa nepodarilo načítať.', en: 'Service accounts could not be loaded.' },

  // --- Actions --------------------------------------------------------------
  rotate: { sk: 'Rotovať token', en: 'Rotate token' },
  deactivate: { sk: 'Deaktivovať', en: 'Deactivate' },
  activate: { sk: 'Aktivovať', en: 'Activate' },
  rotateTitle: { sk: 'Rotovať token účtu „{name}“?', en: 'Rotate the token of “{name}”?' },
  rotateBody: {
    sk: 'Vygeneruje sa nový token a STARÝ TOKEN PRESTANE PLATIŤ OKAMŽITE. Monitorovaciu sondu, ktorá tento účet používa (napr. PROD monitoring), musíte hneď aktualizovať novým tokenom, inak začne hlásiť chyby.',
    en: 'A new token is generated and the OLD TOKEN STOPS WORKING IMMEDIATELY. The monitoring probe using this account (e.g. PROD monitoring) must be updated with the new token right away, otherwise it starts failing.',
  },
  rotateConfirm: { sk: 'Áno, rotovať token', en: 'Yes, rotate token' },
  deactivateTitle: { sk: 'Deaktivovať účet „{name}“?', en: 'Deactivate “{name}”?' },
  deactivateBody: {
    sk: 'Token účtu okamžite prestane fungovať a monitoring, ktorý ho používa, začne hlásiť chyby. Účet sa nevymaže — neskôr ho môžete znova aktivovať.',
    en: 'The account token stops working immediately and any monitoring using it starts failing. The account is not deleted — you can activate it again later.',
  },
  deactivateConfirm: { sk: 'Áno, deaktivovať', en: 'Yes, deactivate' },
  activateTitle: { sk: 'Aktivovať účet „{name}“?', en: 'Activate “{name}”?' },
  activateBody: {
    sk: 'Existujúci token účtu začne znova fungovať. Ak si nie ste istí, kto ho má, radšej po aktivácii token rotujte.',
    en: 'The existing account token becomes valid again. If you are unsure who holds it, rotate the token after activating.',
  },
  activateConfirm: { sk: 'Áno, aktivovať', en: 'Yes, activate' },

  // --- One-time token -------------------------------------------------------
  tokenCreatedTitle: { sk: 'Servisný účet vytvorený', en: 'Service account created' },
  tokenRotatedTitle: { sk: 'Token bol rotovaný', en: 'Token rotated' },
  tokenFor: { sk: 'Token pre účet {name}:', en: 'Token for account {name}:' },
  tokenWarning: {
    sk: 'Token sa zobrazí iba raz. Skopírujte ho a uložte do trezoru (napr. password manager / secret store). Po zatvorení tohto okna ho už nebude možné zobraziť — ak sa stratí, token rotujte.',
    en: 'The token is shown only once. Copy it and store it in a vault (e.g. password manager / secret store). After closing this dialog it cannot be shown again — if it is lost, rotate the token.',
  },
  tokenRotatedHint: {
    sk: 'Starý token už neplatí. Aktualizujte monitorovaciu sondu týmto novým tokenom.',
    en: 'The old token no longer works. Update the monitoring probe with this new token.',
  },
  copy: { sk: 'Kopírovať', en: 'Copy' },
  copied: { sk: 'Skopírované', en: 'Copied' },
  copyFailed: {
    sk: 'Kopírovanie zlyhalo — označte token a skopírujte ho ručne.',
    en: 'Copy failed — select the token and copy it manually.',
  },
  tokenSavedClose: { sk: 'Token mám uložený, zavrieť', en: 'I have stored the token, close' },

  // --- Outcomes / errors ----------------------------------------------------
  deactivatedOk: { sk: 'Účet „{name}“ bol deaktivovaný.', en: '“{name}” was deactivated.' },
  activatedOk: { sk: 'Účet „{name}“ bol aktivovaný.', en: '“{name}” was activated.' },
  errorDuplicate: {
    sk: 'Servisný účet s týmto názvom už existuje.',
    en: 'A service account with this name already exists.',
  },
  errorForbidden: {
    sk: 'Na správu servisných účtov potrebujete rolu system_admin.',
    en: 'Managing service accounts requires the system_admin role.',
  },
  errorValidation: { sk: 'Neplatné údaje: {detail}', en: 'Invalid data: {detail}' },
  errorGeneric: { sk: 'Požiadavka zlyhala.', en: 'Request failed.' },
} satisfies Record<string, BiText>

export type ServiceAccountTextKey = keyof typeof SERVICE_ACCOUNT_TEXTS

/** Translate a service-accounts string, filling `{placeholders}`. */
export function sat(
  key: ServiceAccountTextKey,
  vars: Record<string, string | number | null | undefined> = {},
  lang: Lang = getLang(),
): string {
  return SERVICE_ACCOUNT_TEXTS[key][lang].replace(/\{(\w+)\}/g, (_, name: string) =>
    String(vars[name] ?? ''),
  )
}

/** Server-side name rule (BE #64): `^[a-z0-9][a-z0-9-]{2,62}$`. */
export const SERVICE_ACCOUNT_NAME_RE = /^[a-z0-9][a-z0-9-]{2,62}$/

/** Map an API error to a translated, user-facing message (never echoes tokens). */
export function serviceAccountErrorMessage(error: unknown, lang: Lang = getLang()): string {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })?.response
  const status = response?.status
  if (status === 409) return sat('errorDuplicate', {}, lang)
  if (status === 403) return sat('errorForbidden', {}, lang)
  if (status === 422) {
    const detail = response?.data?.detail
    const text = Array.isArray(detail)
      ? detail
          .map((d) => (d && typeof d === 'object' && 'msg' in d ? String((d as { msg: unknown }).msg) : ''))
          .filter(Boolean)
          .join('; ')
      : typeof detail === 'string'
        ? detail
        : ''
    return text ? sat('errorValidation', { detail: text }, lang) : sat('fieldNameInvalid', {}, lang)
  }
  const detail = response?.data?.detail
  if (typeof detail === 'string' && detail) return detail
  return sat('errorGeneric', {}, lang)
}
